import { normalizeFileAccessUrlForBrowser } from '@/utils/fileAccessUrl';

export const IMAGE_LIGHTBOX_SAVE_DEBOUNCE_MS = 600;

type OutputMeta = {
  mime: string;
  name: string;
  quality?: number;
};

type BitmapLike = {
  width: number;
  height: number;
  naturalWidth?: number;
  naturalHeight?: number;
  close?: () => void;
} & CanvasImageSource;

function stemOf(fileName: string): string {
  const base = (fileName || 'image').replace(/[/\\?*:"<>|]/g, '_').trim() || 'image';
  return base.replace(/\.[^.]+$/, '') || 'image';
}

export function rotatedOutputMeta(fileName: string | undefined, sourceType: string): OutputMeta {
  const base = (fileName || 'image').replace(/[/\\?*:"<>|]/g, '_').trim() || 'image';
  const stem = stemOf(base);
  const src = String(sourceType || '').toLowerCase();
  if (src.includes('jpeg') || src === 'image/jpg' || /\.jpe?g$/i.test(base)) {
    return { mime: 'image/jpeg', name: `${stem}.jpg`, quality: 0.92 };
  }
  if (src === 'image/png' || /\.png$/i.test(base)) {
    return { mime: 'image/png', name: `${stem}.png` };
  }
  if (
    src === 'image/gif'
    || /\.gif$/i.test(base)
    || src === 'image/webp'
    || /\.webp$/i.test(base)
    || src === 'image/svg+xml'
    || /\.svg$/i.test(base)
  ) {
    return { mime: 'image/png', name: `${stem}.png` };
  }
  return { mime: 'image/jpeg', name: `${stem}.jpg`, quality: 0.92 };
}

function normalizeDegrees(degrees: number): number {
  return ((Math.round(degrees / 90) * 90) % 360 + 360) % 360;
}

export function sniffImageMime(bytes: Uint8Array): string | null {
  if (bytes.length >= 8
    && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return 'image/png';
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (bytes.length >= 6
    && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
    return 'image/gif';
  }
  if (bytes.length >= 12
    && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
    return 'image/webp';
  }
  return null;
}

async function readBlobArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    try {
      return await blob.arrayBuffer();
    } catch {
      // fall through
    }
  }
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error('读取图片失败'));
    reader.readAsArrayBuffer(blob);
  });
}

function mimeFromFileName(fileName?: string): string | null {
  const name = String(fileName || '').toLowerCase();
  if (/\.png$/i.test(name)) return 'image/png';
  if (/\.jpe?g$/i.test(name)) return 'image/jpeg';
  if (/\.gif$/i.test(name)) return 'image/gif';
  if (/\.webp$/i.test(name)) return 'image/webp';
  return null;
}

export async function ensureDecodableImageBlob(
  blob: Blob,
  fileName?: string,
): Promise<Blob> {
  let sniffed: string | null = null;
  try {
    const buffer = await readBlobArrayBuffer(blob);
    sniffed = sniffImageMime(new Uint8Array(buffer.slice(0, 16)));
    const current = String(blob.type || '').toLowerCase();
    const needsRetype = !current.startsWith('image/')
      || current === 'image/svg+xml'
      || (!!sniffed && current !== sniffed);
    if (sniffed && needsRetype) {
      return new Blob([buffer], { type: sniffed });
    }
  } catch {
    // 魔数嗅探失败时按扩展名兜底
  }
  const byName = mimeFromFileName(fileName);
  const current = String(blob.type || '').toLowerCase();
  if (byName && (!current.startsWith('image/') || current === 'image/svg+xml')) {
    const buffer = await readBlobArrayBuffer(blob);
    return new Blob([buffer], { type: byName });
  }
  return blob;
}

async function fetchImageBlob(src: string, fileName?: string): Promise<Blob> {
  const url = normalizeFileAccessUrlForBrowser(src);
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`读取图片失败（${res.status}）`);
  }
  const blob = await res.blob();
  if (!blob || blob.size <= 0) {
    throw new Error('图片内容为空');
  }
  return ensureDecodableImageBlob(blob, fileName);
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('导出旋转图片失败'));
        return;
      }
      resolve(blob);
    }, mime, quality);
  });
}

function closeBitmap(bitmap: BitmapLike) {
  bitmap.close?.();
}

function sourceSize(source: BitmapLike) {
  const width = source.naturalWidth || source.width;
  const height = source.naturalHeight || source.height;
  return { width, height };
}

async function drawDecodedToCanvas(
  source: BitmapLike,
  degrees: number,
  closeAfter: boolean,
): Promise<HTMLCanvasElement> {
  const { width, height } = sourceSize(source);
  const swap = degrees === 90 || degrees === 270;
  const canvas = document.createElement('canvas');
  canvas.width = swap ? height : width;
  canvas.height = swap ? width : height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    if (closeAfter) closeBitmap(source);
    throw new Error('当前浏览器无法旋转图片');
  }
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  try {
    ctx.drawImage(source, -width / 2, -height / 2);
  } catch (e) {
    if (closeAfter) closeBitmap(source);
    throw decodeImageError(e);
  }
  if (closeAfter) closeBitmap(source);
  return canvas;
}

function decodeImageError(e: unknown): Error {
  const msg = e instanceof Error ? e.message : String(e || '');
  if (/source image could not be decoded/i.test(msg)) {
    return new Error('图片解码失败，请重新打开后再旋转');
  }
  return e instanceof Error ? e : new Error(msg || '图片解码失败');
}

function loadImageElement(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('图片解码失败'));
    };
    img.src = url;
  });
}

async function decodeBlobToBitmap(blob: Blob, fileName?: string): Promise<BitmapLike> {
  const typed = await ensureDecodableImageBlob(blob, fileName);
  try {
    return await loadImageElement(typed);
  } catch {
    // 继续尝试 createImageBitmap；错误 MIME 时 Image() 也可能失败。
  }
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(typed);
    } catch (e) {
      throw decodeImageError(e);
    }
  }
  throw new Error('图片解码失败');
}

async function exportRotatedCanvas(
  canvas: HTMLCanvasElement,
  fileName: string | undefined,
  sourceType: string,
): Promise<File> {
  const meta = rotatedOutputMeta(fileName, sourceType);
  const out = await canvasToBlob(canvas, meta.mime, meta.quality);
  return new File([out], meta.name, { type: meta.mime });
}

/** 使用页面上已经成功显示的图片烘焙旋转，避免再次 fetch/createImageBitmap 失败。 */
export async function rotateRenderedImage(
  image: HTMLImageElement,
  degrees: number,
  fileName?: string,
): Promise<File> {
  const normalized = normalizeDegrees(degrees);
  if (normalized === 0) {
    throw new Error('当前角度无需保存');
  }
  if (!image.naturalWidth || !image.naturalHeight) {
    throw new Error('图片尚未加载完成');
  }
  const canvas = await drawDecodedToCanvas(image, normalized, false);
  return exportRotatedCanvas(canvas, fileName, fileName || '');
}

/** 将图片按 90° 倍数烘焙为新文件；GIF/WebP/SVG 落成 PNG，避免动画被拆仍沿用原扩展名。 */
export async function rotateImageBlob(
  src: string,
  degrees: number,
  fileName?: string,
): Promise<File> {
  const normalized = normalizeDegrees(degrees);
  if (normalized === 0) {
    throw new Error('当前角度无需保存');
  }
  const blob = await fetchImageBlob(src, fileName);
  const bitmap = await decodeBlobToBitmap(blob, fileName);
  const canvas = await drawDecodedToCanvas(bitmap, normalized, true);
  return exportRotatedCanvas(canvas, fileName, blob.type || fileName || '');
}
